import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

import App from '@/App';
import { AppSettings } from '@/AppSettings';
import ServiceStackClientProvider from '@/components/providers/api/ServiceStackClientProvider';
import AuthenticatedUserProvider from '@/components/providers/authentication/AuthenticatedUserProvider';
import LinkProvider from '@/components/providers/layouts/LinkProvider';
import LoadingProvider from '@/components/providers/layouts/LoadingProvider';
import ChosenThemeProvider from '@/components/providers/styles/ChosenThemeProvider';
import ThemeProvider from '@/components/providers/styles/ThemeProvider';
import { LocalizationProvider, enUS } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { SnackbarProvider } from 'notistack';
import { SWRConfig } from 'swr';
import { Customer } from './api/dtos';
import ChosenThemeSizeProvider from './components/providers/styles/ChosenThemeSizeProvider';
import DefaultAfterThemeCreated from './styles/DefaultAfterThemeCreated';
import DefaultBeforeThemeCreated from './styles/DefaultBeforeThemeCreated';
import * as Sentry from "@sentry/react";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "react-router-dom";
// const test = async () => {
//     const { toUpper } = await import('wasm/main.js');
//     console.log(await toUpper('test'));
// }

// test();

import bctAppleTouchIcon114x114 from '@/assets/bct/favicon/apple-touch-icon-114x114.png';
import bctAppleTouchIcon120x120 from '@/assets/bct/favicon/apple-touch-icon-120x120.png';
import bctAppleTouchIcon144x144 from '@/assets/bct/favicon/apple-touch-icon-144x144.png';
import bctAppleTouchIcon152x152 from '@/assets/bct/favicon/apple-touch-icon-152x152.png';
import bctAppleTouchIcon57x57 from '@/assets/bct/favicon/apple-touch-icon-57x57.png';
import bctAppleTouchIcon60x60 from '@/assets/bct/favicon/apple-touch-icon-60x60.png';
import bctAppleTouchIcon72x72 from '@/assets/bct/favicon/apple-touch-icon-72x72.png';
import bctAppleTouchIcon76x76 from '@/assets/bct/favicon/apple-touch-icon-76x76.png';
import bctFavIcon128x128 from '@/assets/bct/favicon/favicon-128.png';
import bctFavIcon16x16 from '@/assets/bct/favicon/favicon-16x16.png';
import bctFavIcon196x196 from '@/assets/bct/favicon/favicon-196x196.png';
import bctFavIcon32x32 from '@/assets/bct/favicon/favicon-32x32.png';
import bctFavIcon96x96 from '@/assets/bct/favicon/favicon-96x96.png';
import bctApplicationTitleImage from '@/assets/bct/favicon/mstile-144x144.png';
import bctApplicationSquare150x150 from '@/assets/bct/favicon/mstile-150x150.png';
import bctApplicationSquare310x150 from '@/assets/bct/favicon/mstile-310x150.png';
import bctApplicationSquare310x310 from '@/assets/bct/favicon/mstile-310x310.png';
import bctApplicationSquare70x70 from '@/assets/bct/favicon/mstile-70x70.png';

import trogAppleTouchIcon114x114 from '@/assets/trog/favicon/apple-touch-icon-114x114.png';
import trogAppleTouchIcon120x120 from '@/assets/trog/favicon/apple-touch-icon-120x120.png';
import trogAppleTouchIcon144x144 from '@/assets/trog/favicon/apple-touch-icon-144x144.png';
import trogAppleTouchIcon152x152 from '@/assets/trog/favicon/apple-touch-icon-152x152.png';
import trogAppleTouchIcon57x57 from '@/assets/trog/favicon/apple-touch-icon-57x57.png';
import trogAppleTouchIcon60x60 from '@/assets/trog/favicon/apple-touch-icon-60x60.png';
import trogAppleTouchIcon72x72 from '@/assets/trog/favicon/apple-touch-icon-72x72.png';
import trogAppleTouchIcon76x76 from '@/assets/trog/favicon/apple-touch-icon-76x76.png';
import trogFavIcon128x128 from '@/assets/trog/favicon/favicon-128.png';
import trogFavIcon16x16 from '@/assets/trog/favicon/favicon-16x16.png';
import trogFavIcon196x196 from '@/assets/trog/favicon/favicon-196x196.png';
import trogFavIcon32x32 from '@/assets/trog/favicon/favicon-32x32.png';
import trogFavIcon96x96 from '@/assets/trog/favicon/favicon-96x96.png';
import trogApplicationTitleImage from '@/assets/trog/favicon/mstile-144x144.png';
import trogApplicationSquare150x150 from '@/assets/trog/favicon/mstile-150x150.png';
import trogApplicationSquare310x150 from '@/assets/trog/favicon/mstile-310x150.png';
import trogApplicationSquare310x310 from '@/assets/trog/favicon/mstile-310x310.png';
import trogApplicationSquare70x70 from '@/assets/trog/favicon/mstile-70x70.png';
import SignalRProvider from './components/providers/api/SignalRProvider';
import { createSentryAnonymousUser } from './errors';
import ExternalUsersProvider from './components/providers/data/ExternalUsersProvider';

if (AppSettings.sentry.dsn) {
    const integrations = [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.browserTracingIntegration(),
        //Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [window.location.origin],
            networkCaptureBodies: true
        })
    ];

    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: "dark",
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: "fullName",
                email: "email",
            },
        }));
    }

    Sentry.init({
        dsn: AppSettings.sentry.dsn,
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        enableMetrics: AppSettings.sentry.enableMetrics,
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        debug: AppSettings.sentry.debug,
        integrations: integrations,
        environment: AppSettings.sentry.environment,
        release: AppSettings.sentry.release,
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        tracePropagationTargets: ["localhost", new RegExp(`/^${window.location.origin}`)],
        profileLifecycle: "trace",
        profileSessionSampleRate: AppSettings.sentry.profileSessionSampleRate,

        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        
        // Enable logs to be sent to Sentry
        enableLogs: AppSettings.sentry.enableLogs,
    });

    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);

    Sentry.setUser(createSentryAnonymousUser());
}

interface IFavIconMapping {
    title?: string;
    appleTouchIcon57x57?: string;
    appleTouchIcon114x114?: string;
    appleTouchIcon72x72?: string;
    appleTouchIcon144x144?: string;
    appleTouchIcon60x60?: string;
    appleTouchIcon120x120?: string;
    appleTouchIcon76x76?: string;
    appleTouchIcon152x152?: string;
    favIcon196x196?: string;
    favIcon96x96?: string;
    favIcon32x32?: string;
    favIcon16x16?: string;
    favIcon128x128?: string;
    applicationName?: string;
    applicationTitleColor?: string;
    applicationTitleImage?: string;
    applicationSquare70x70?: string;
    applicationSquare150x150?: string;
    applicationSquare310x150?: string;
    applicationSquare310x310?: string;
}

const FavIconMappings: Record<Customer, IFavIconMapping> = {
    [Customer.Bct]: {
        title: 'Source Data Verification | Breast Cancer Trials',
        appleTouchIcon57x57: bctAppleTouchIcon57x57,
        appleTouchIcon114x114: bctAppleTouchIcon114x114,
        appleTouchIcon72x72: bctAppleTouchIcon72x72,
        appleTouchIcon144x144: bctAppleTouchIcon144x144,
        appleTouchIcon60x60: bctAppleTouchIcon60x60,
        appleTouchIcon120x120: bctAppleTouchIcon120x120,
        appleTouchIcon76x76: bctAppleTouchIcon76x76,
        appleTouchIcon152x152: bctAppleTouchIcon152x152,
        favIcon196x196: bctFavIcon196x196,
        favIcon96x96: bctFavIcon96x96,
        favIcon32x32: bctFavIcon32x32,
        favIcon16x16: bctFavIcon16x16,
        favIcon128x128: bctFavIcon128x128,
        applicationName: 'Source Data Verification | Breast Cancer Trials',
        applicationTitleColor: '#FFFFFF',
        applicationTitleImage: bctApplicationTitleImage,
        applicationSquare70x70: bctApplicationSquare70x70,
        applicationSquare150x150: bctApplicationSquare150x150,
        applicationSquare310x150: bctApplicationSquare310x150,
        applicationSquare310x310: bctApplicationSquare310x310
    },
    [Customer.Trog]: {
        title: 'Source Data Verification | TROG Cancer Research',
        appleTouchIcon57x57: trogAppleTouchIcon57x57,
        appleTouchIcon114x114: trogAppleTouchIcon114x114,
        appleTouchIcon72x72: trogAppleTouchIcon72x72,
        appleTouchIcon144x144: trogAppleTouchIcon144x144,
        appleTouchIcon60x60: trogAppleTouchIcon60x60,
        appleTouchIcon120x120: trogAppleTouchIcon120x120,
        appleTouchIcon76x76: trogAppleTouchIcon76x76,
        appleTouchIcon152x152: trogAppleTouchIcon152x152,
        favIcon196x196: trogFavIcon196x196,
        favIcon96x96: trogFavIcon96x96,
        favIcon32x32: trogFavIcon32x32,
        favIcon16x16: trogFavIcon16x16,
        favIcon128x128: trogFavIcon128x128,
        applicationName: 'Source Data Verification | TROG Cancer Research',
        applicationTitleColor: '#FFFFFF',
        applicationTitleImage: trogApplicationTitleImage,
        applicationSquare70x70: trogApplicationSquare70x70,
        applicationSquare150x150: trogApplicationSquare150x150,
        applicationSquare310x150: trogApplicationSquare310x150,
        applicationSquare310x310: trogApplicationSquare310x310
    },
    [Customer.Unknown]: {

    }

}

const addTitle = (title?: string) => {
    if (!title) {
        return;
    }

    const titleElement = document.createElement('title');
    titleElement.innerText = title;
    document.head.appendChild(titleElement);
}

const addAppleTouchIcon = (image?: string, size?: string) => {
    if (!image || !size) {
        return;
    }

    
    const linkElement = document.createElement('link');
    linkElement.href = image;
    //@ts-ignore
    linkElement.sizes = size;
    linkElement.rel = 'apple-touch-icon-precomposed';
    document.head.appendChild(linkElement);
}

const addFavIcon = (image?: string, size?: string) => {
    if (!image || !size) {
        return;
    }

    
    const linkElement = document.createElement('link');
    linkElement.href = image;
    //@ts-ignore
    linkElement.sizes = size;
    linkElement.rel = 'icon';
    linkElement.type = 'image/png';
    document.head.appendChild(linkElement);
}

const addMeta = (name?: string, content?: string) => {
    if (!name || !content) {
        return;
    }

    
    const metaElement = document.createElement('meta');
    metaElement.name = name;
    metaElement.content = content;
    document.head.appendChild(metaElement);
}

const updateHead = (customer: Customer) => {
    const mapping: IFavIconMapping = FavIconMappings[customer];

    addTitle(mapping.title);

    addAppleTouchIcon(mapping.appleTouchIcon57x57, '57x57');
    addAppleTouchIcon(mapping.appleTouchIcon114x114, '114x114');
    addAppleTouchIcon(mapping.appleTouchIcon72x72, '72x72');
    addAppleTouchIcon(mapping.appleTouchIcon144x144, '144x144');
    addAppleTouchIcon(mapping.appleTouchIcon60x60, '60x60');
    addAppleTouchIcon(mapping.appleTouchIcon120x120, '120x120');
    addAppleTouchIcon(mapping.appleTouchIcon76x76, '76x76');
    addAppleTouchIcon(mapping.appleTouchIcon152x152, '152x152');

    addFavIcon(mapping.favIcon196x196, '196x196');
    addFavIcon(mapping.favIcon96x96, '96x96');
    addFavIcon(mapping.favIcon32x32, '32x32');
    addFavIcon(mapping.favIcon16x16, '16x16');
    addFavIcon(mapping.favIcon128x128, '128x128');

    addMeta('application-name', mapping.applicationName);
    addMeta('msapplication-TileColor', mapping.applicationTitleColor);
    addMeta('msapplication-TileImage', mapping.applicationTitleImage);
    addMeta('msapplication-square70x70logo', mapping.applicationSquare70x70);
    addMeta('msapplication-square150x150logo', mapping.applicationSquare150x150);
    addMeta('msapplication-wide310x150logo', mapping.applicationSquare310x150);
    addMeta('msapplication-square310x310logo', mapping.applicationSquare310x310);
}

const Main = () => {
    return (
        <LocalizationProvider
            localeText={enUS.components.MuiLocalizationProvider.defaultProps.localeText}
            dateAdapter={AdapterLuxon}
        >
            <SWRConfig
                value={{
                    revalidateOnFocus: false
                }}
            >
                <LoadingProvider>
                    <ServiceStackClientProvider baseUrl={AppSettings.client?.baseUrl} basePath={AppSettings.client?.basePath}>
                        <AuthenticatedUserProvider>
                            <SignalRProvider baseUrl={AppSettings.client?.eventStreamUrl ?? '/'} debug={AppSettings.client?.eventStreamDebug}>
                                <ChosenThemeSizeProvider>
                                    <ChosenThemeProvider override={AppSettings.overrideTheme}>
                                        <ThemeProvider
                                            beforeThemeCreated={DefaultBeforeThemeCreated}
                                            afterThemeCreated={DefaultAfterThemeCreated}
                                        >
                                            <LinkProvider>
                                                <SnackbarProvider
                                                    anchorOrigin={{
                                                        horizontal: 'right',
                                                        vertical: 'bottom'
                                                    }}
                                                    preventDuplicate={true}
                                                >
                                                    <ExternalUsersProvider>
                                                        <App />
                                                    </ExternalUsersProvider>
                                                </SnackbarProvider>
                                            </LinkProvider>
                                        </ThemeProvider>
                                    </ChosenThemeProvider>
                                </ChosenThemeSizeProvider>
                            </SignalRProvider>
                        </AuthenticatedUserProvider>
                    </ServiceStackClientProvider>
                </LoadingProvider>
            </SWRConfig>
        </LocalizationProvider>
    )
}

//const ProfiledMain = Sentry.withProfiler(Main);

document.addEventListener('DOMContentLoaded', function(event) {
    updateHead(AppSettings.customer);


    const root = AppSettings.sentry.dsn ? 
        createRoot(
            document.getElementById('root')!,
            {
                // Callback called when React automatically recovers from errors.
                onRecoverableError: Sentry.reactErrorHandler(),
            }
        ) :
        createRoot(document.getElementById('root')!);

    root.render(
        <StrictMode>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                <Main />
            </Sentry.ErrorBoundary>
        </StrictMode>
    )
});